import { useEffect, useState } from "react";
import {
    UserRound,
    Plus,
    Trash2,
    Mail,
    Phone,
    Star,
    StarOff,
    Pencil,
    Clock,
    KeyRound,
    Send,
    Check,
    History,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

const EMPTY_CONTACT = {
    first_name: "",
    last_name: "",
    role: "",
    email: "",
    phone: "",
    is_primary: false,
    notes: "",
};

// ─────────────────────────────────────────────────────────────
// DossierContactsPanel — opérateurs/interlocuteurs du dossier
// ─────────────────────────────────────────────────────────────

export function DossierContactsPanel({ dossierId }) {
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState(EMPTY_CONTACT);
    const [editId, setEditId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        const { data } = await cockpit.get(
            `/dossiers/${dossierId}/contacts`,
        );
        setItems(data);
    };

    useEffect(() => {
        reload();
    }, [dossierId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!draft.first_name.trim() || !draft.last_name.trim()) return;
        setBusy(true);
        try {
            if (editId) {
                await cockpit.put(
                    `/dossiers/${dossierId}/contacts/${editId}`,
                    draft,
                );
            } else {
                await cockpit.post(
                    `/dossiers/${dossierId}/contacts`,
                    draft,
                );
            }
            setDraft(EMPTY_CONTACT);
            setEditId(null);
            setShowForm(false);
            await reload();
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (c) => {
        setEditId(c.id);
        setDraft({ ...c });
        setShowForm(true);
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cet interlocuteur ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/contacts/${id}`);
        await reload();
    };

    const togglePrimary = async (c) => {
        await cockpit.put(
            `/dossiers/${dossierId}/contacts/${c.id}`,
            { is_primary: !c.is_primary },
        );
        await reload();
    };

    const sorted = [...items].sort(
        (a, b) => Number(b.is_primary) - Number(a.is_primary),
    );

    return (
        <section
            data-testid="cockpit-dossier-contacts"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <UserRound
                            strokeWidth={1}
                            size={14}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Fiche client
                    </div>
                    <h2
                        className="font-serif text-[24px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Interlocuteurs du dossier
                    </h2>
                    <p className="mt-2 text-[12px] text-[#f4ecd8]/45 italic font-light max-w-2xl">
                        Carnet d'adresses opérationnel. Le contact marqué
                        comme principal apparaît en premier.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="contact-add-toggle"
                    onClick={() => {
                        setEditId(null);
                        setDraft(EMPTY_CONTACT);
                        setShowForm((v) => !v);
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                >
                    <Plus strokeWidth={1.2} size={12} />
                    {showForm ? "Fermer" : "Ajouter un interlocuteur"}
                </button>
            </div>

            {showForm && (
                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="bg-[#070d18] border border-white/[0.08] p-6 lg:p-8 mb-8"
                >
                    <div className="overline text-[#d4af37]/80 mb-5">
                        {editId ? "Modifier l'interlocuteur" : "Nouvel interlocuteur"}
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        <Field
                            label="Prénom *"
                            value={draft.first_name}
                            onChange={(e) =>
                                setDraft({ ...draft, first_name: e.target.value })
                            }
                            testid="contact-first-name"
                            required
                        />
                        <Field
                            label="Nom *"
                            value={draft.last_name}
                            onChange={(e) =>
                                setDraft({ ...draft, last_name: e.target.value })
                            }
                            testid="contact-last-name"
                            required
                        />
                        <Field
                            label="Rôle"
                            value={draft.role}
                            onChange={(e) =>
                                setDraft({ ...draft, role: e.target.value })
                            }
                            placeholder="DG, Family Officer, Avocat…"
                            testid="contact-role"
                        />
                        <Field
                            label="Email"
                            type="email"
                            value={draft.email}
                            onChange={(e) =>
                                setDraft({ ...draft, email: e.target.value })
                            }
                            placeholder="prenom.nom@exemple.com"
                            testid="contact-email"
                        />
                        <Field
                            label="Téléphone"
                            value={draft.phone}
                            onChange={(e) =>
                                setDraft({ ...draft, phone: e.target.value })
                            }
                            placeholder="+33 6 …"
                            testid="contact-phone"
                        />
                        <label className="flex items-end gap-3 pb-2 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="contact-primary"
                                checked={!!draft.is_primary}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        is_primary: e.target.checked,
                                    })
                                }
                                className="w-4 h-4 accent-[#d4af37]"
                            />
                            <span className="text-[11px] tracking-[0.22em] uppercase text-[#f4ecd8]/70">
                                Interlocuteur principal
                            </span>
                        </label>
                    </div>
                    <div className="mt-5">
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Notes
                        </label>
                        <textarea
                            data-testid="contact-notes"
                            rows={3}
                            value={draft.notes}
                            onChange={(e) =>
                                setDraft({ ...draft, notes: e.target.value })
                            }
                            placeholder="Préférences de contact, contexte, mandats représentés…"
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] leading-[1.7] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 resize-y"
                        />
                    </div>
                    <div className="mt-7 flex flex-wrap gap-3 justify-end">
                        {editId && (
                            <button
                                type="button"
                                onClick={() => {
                                    setEditId(null);
                                    setDraft(EMPTY_CONTACT);
                                }}
                                className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-white/15 text-[#f4ecd8]/60 hover:text-[#f4ecd8] hover:border-white/30 transition-all"
                            >
                                Annuler la modification
                            </button>
                        )}
                        <button
                            type="submit"
                            data-testid="contact-submit"
                            disabled={busy || !draft.first_name.trim() || !draft.last_name.trim()}
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {editId ? "Enregistrer" : "Ajouter au carnet"}
                        </button>
                    </div>
                </form>
            )}

            {sorted.length === 0 ? (
                <p
                    data-testid="contact-empty"
                    className="text-[12px] text-[#f4ecd8]/40 italic font-light py-6"
                >
                    Aucun interlocuteur renseigné pour ce dossier.
                </p>
            ) : (
                <div className="grid md:grid-cols-2 gap-5">
                    {sorted.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`contact-card-${c.id}`}
                            className={`relative bg-[#070d18] border p-6 ${
                                c.is_primary
                                    ? "border-[#d4af37]/40"
                                    : "border-white/[0.08]"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="min-w-0">
                                    <div
                                        className="font-serif text-[20px] text-[#f4ecd8] leading-tight"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {c.first_name} {c.last_name}
                                    </div>
                                    {c.role && (
                                        <div className="mt-1 text-[12px] text-[#d4af37]/80 tracking-wide">
                                            {c.role}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => togglePrimary(c)}
                                    title={
                                        c.is_primary
                                            ? "Retirer du contact principal"
                                            : "Définir comme contact principal"
                                    }
                                    data-testid={`contact-primary-${c.id}`}
                                    className={`p-1.5 transition-colors ${
                                        c.is_primary
                                            ? "text-[#d4af37]"
                                            : "text-[#f4ecd8]/30 hover:text-[#d4af37]"
                                    }`}
                                >
                                    {c.is_primary ? (
                                        <Star strokeWidth={1.5} size={16} fill="#d4af37" />
                                    ) : (
                                        <StarOff strokeWidth={1.5} size={16} />
                                    )}
                                </button>
                            </div>
                            <div className="space-y-1.5 text-[13px] text-[#f4ecd8]/80 font-serif">
                                {c.email && (
                                    <a
                                        href={`mailto:${c.email}`}
                                        className="flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                                    >
                                        <Mail
                                            strokeWidth={1.2}
                                            size={12}
                                            className="text-[#d4af37]/60"
                                        />
                                        {c.email}
                                    </a>
                                )}
                                {c.phone && (
                                    <a
                                        href={`tel:${c.phone}`}
                                        className="flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                                    >
                                        <Phone
                                            strokeWidth={1.2}
                                            size={12}
                                            className="text-[#d4af37]/60"
                                        />
                                        {c.phone}
                                    </a>
                                )}
                            </div>
                            {c.notes && (
                                <p className="mt-4 text-[12.5px] text-[#f4ecd8]/55 italic leading-[1.7] font-light whitespace-pre-line border-l-2 border-[#d4af37]/20 pl-3">
                                    {c.notes}
                                </p>
                            )}
                            {c.is_primary && (
                                <span className="absolute top-3 right-12 text-[8.5px] tracking-[0.32em] uppercase text-[#d4af37]/75">
                                    · Principal
                                </span>
                            )}
                            <div className="mt-5 pt-4 border-t border-white/[0.06] flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => startEdit(c)}
                                    data-testid={`contact-edit-${c.id}`}
                                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 text-[#f4ecd8]/55 hover:text-[#d4af37] transition-colors"
                                >
                                    <Pencil strokeWidth={1.3} size={11} />
                                    Modifier
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(c.id)}
                                    data-testid={`contact-delete-${c.id}`}
                                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 text-[#f4ecd8]/40 hover:text-[#e89090] transition-colors"
                                >
                                    <Trash2 strokeWidth={1.3} size={11} />
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// ClientAccessPanel — clients connectés au portail + resend
// ─────────────────────────────────────────────────────────────

function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
}

function ageLabel(iso) {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "à l'instant";
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    return `il y a ${d} j`;
}

export function ClientAccessPanel({ dossierId }) {
    const [clients, setClients] = useState([]);
    const [activity, setActivity] = useState([]);
    const [resendingFor, setResendingFor] = useState(null);
    const [msg, setMsg] = useState("");
    const [loaded, setLoaded] = useState(false);

    const reload = async () => {
        const [cl, act] = await Promise.all([
            cockpit
                .get(`/dossiers/${dossierId}/clients`)
                .then((r) => r.data)
                .catch(() => []),
            cockpit
                .get(`/dossiers/${dossierId}/client-activity`)
                .then((r) => r.data)
                .catch(() => []),
        ]);
        setClients(cl);
        setActivity(act);
        setLoaded(true);
    };

    useEffect(() => {
        reload();
    }, [dossierId]);

    const resend = async (email) => {
        if (
            !window.confirm(
                `Renvoyer un nouveau lien d'accès à ${email} ?\n` +
                "Le précédent mot de passe restera valide jusqu'à la prochaine activation du nouveau lien.",
            )
        )
            return;
        setResendingFor(email);
        setMsg("");
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossierId}/clients/resend-access`,
                { email },
            );
            if (data.ok) {
                setMsg(`Lien envoyé à ${email} ✓`);
            } else {
                setMsg(
                    `Lien généré mais email non envoyé : ${data.email_error || "erreur inconnue"}. URL : ${data.invite_url}`,
                );
            }
            setTimeout(() => setMsg(""), 6000);
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Erreur lors du renvoi.");
        } finally {
            setResendingFor(null);
        }
    };

    if (loaded && clients.length === 0 && activity.length === 0) {
        return (
            <section
                data-testid="cockpit-client-access-empty"
                className="border-t border-white/[0.06] py-12"
            >
                <div className="overline text-[#d4af37]/70 mb-3">
                    <KeyRound
                        strokeWidth={1}
                        size={14}
                        className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                    />
                    Accès client
                </div>
                <h2
                    className="font-serif text-[24px] text-[#f4ecd8] mb-3"
                    style={{ fontWeight: 400 }}
                >
                    Aucun client connecté à ce dossier
                </h2>
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light max-w-2xl">
                    Utilisez le bloc "Invitations" ci-dessus pour créer un
                    accès ; une fois activé, vous verrez ici les connexions
                    et pourrez renvoyer les identifiants.
                </p>
            </section>
        );
    }

    return (
        <section
            data-testid="cockpit-client-access"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <KeyRound
                            strokeWidth={1}
                            size={14}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Accès client
                    </div>
                    <h2
                        className="font-serif text-[24px] text-[#f4ecd8] mb-2"
                        style={{ fontWeight: 400 }}
                    >
                        Comptes connectés
                    </h2>
                    <p className="text-[12px] text-[#f4ecd8]/45 italic font-light mb-6">
                        Renvoyer le lien génère un nouveau mot de passe à
                        définir par le client — pas de partage manuel.
                    </p>
                    {clients.length === 0 ? (
                        <p className="text-[12px] text-[#f4ecd8]/40 italic">
                            Aucun compte client actif.
                        </p>
                    ) : (
                        <ul className="space-y-3">
                            {clients.map((c) => (
                                <li
                                    key={c.id || c.email}
                                    data-testid={`client-account-${c.email}`}
                                    className="bg-[#070d18] border border-white/[0.08] p-5"
                                >
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="min-w-0">
                                            <div className="font-serif text-[16px] text-[#f4ecd8] truncate">
                                                {c.name || c.email}
                                            </div>
                                            <div className="text-[12px] text-[#d4af37]/80 truncate">
                                                {c.email}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-[#f4ecd8]/45 tracking-wide mb-4">
                                        <Clock
                                            strokeWidth={1.2}
                                            size={11}
                                            className="inline-block -mt-0.5 mr-1.5"
                                        />
                                        Dernière connexion :{" "}
                                        <span className="text-[#f4ecd8]/65">
                                            {c.last_login_at
                                                ? `${formatDateTime(c.last_login_at)} · ${ageLabel(c.last_login_at)}`
                                                : "jamais"}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`client-resend-${c.email}`}
                                        onClick={() => resend(c.email)}
                                        disabled={resendingFor === c.email}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                                    >
                                        {resendingFor === c.email ? (
                                            "Envoi en cours…"
                                        ) : (
                                            <>
                                                <Send strokeWidth={1.2} size={12} />
                                                Renvoyer le lien d'accès
                                            </>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {msg && (
                        <div
                            data-testid="client-access-msg"
                            className="mt-5 text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-2 italic leading-relaxed break-all"
                        >
                            {msg}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-7">
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <History
                            strokeWidth={1}
                            size={14}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Journal d'accès
                    </div>
                    <h3
                        className="font-serif text-[20px] text-[#f4ecd8] mb-2"
                        style={{ fontWeight: 400 }}
                    >
                        Dernières connexions
                    </h3>
                    <p className="text-[12px] text-[#f4ecd8]/45 italic font-light mb-6">
                        Horodatage chiffré · audit log conservé pour ce dossier.
                    </p>
                    {activity.length === 0 ? (
                        <p className="text-[12px] text-[#f4ecd8]/40 italic">
                            Aucune connexion enregistrée.
                        </p>
                    ) : (
                        <ul className="border-t border-white/[0.06]">
                            {activity.slice(0, 12).map((a, i) => (
                                <li
                                    key={a.id || i}
                                    className="grid grid-cols-[auto_1fr_auto] items-center gap-4 py-3 border-b border-white/[0.04]"
                                >
                                    <span
                                        className={`text-[9px] tracking-[0.28em] uppercase px-2 py-1 border ${
                                            a.action === "redeem"
                                                ? "border-[#d4af37]/50 text-[#d4af37]"
                                                : "border-white/15 text-[#f4ecd8]/60"
                                        }`}
                                    >
                                        {a.action === "redeem"
                                            ? "Activation"
                                            : a.action === "login"
                                              ? "Connexion"
                                              : a.action || "Accès"}
                                    </span>
                                    <span className="text-[13px] font-serif text-[#f4ecd8]/80 truncate">
                                        {a.email}
                                    </span>
                                    <span className="text-[11px] text-[#d4af37]/70 tracking-wide whitespace-nowrap">
                                        {formatDateTime(a.at)}{" "}
                                        <span className="text-[#f4ecd8]/35">
                                            · {ageLabel(a.at)}
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// Local lightweight Field input (tracks all attrs incl. required)
// ─────────────────────────────────────────────────────────────

function Field({
    label,
    value,
    onChange,
    type = "text",
    placeholder = "",
    required = false,
    testid,
}) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                {label}
            </label>
            <input
                data-testid={testid}
                type={type}
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 transition-colors"
            />
        </div>
    );
}

// Make icon imports tree-shake-friendly (Check used as cosmetic indicator)
export const ContactsCheckIcon = Check;
